"use client"

import { useMutation } from "@tanstack/react-query"
import { useTranslations } from "next-intl"
import { useRouter } from "next/navigation"
import { updateMyUiLocale } from "@/server-actions/ui-locale"
import { withQueryHandling } from "@/lib/with-query-handling"
import { useAuth } from "@/hooks/auth/use-auth"
import type { UiLocale } from "@/lib/i18n/locales"

export function useUpdateMyUiLocale() {
  const t = useTranslations("UserProfile")
  const { refreshAuth } = useAuth()
  const router = useRouter()
  return useMutation({
    mutationFn: (input: { locale: UiLocale; setErrors: (errors: Record<string, unknown>) => void }) =>
      withQueryHandling(saveLocale(input.locale), {
        loading: t("languageSaving"), showSuccessToast: false,
        error: t("languageFailed"),
        setErrors: () => input.setErrors({ locale: t("languageUnavailable") }),
      }),
    onSuccess: async () => {
      // The write succeeded. Refresh the server provider even if the session poll is temporarily unavailable.
      await refreshAuth().catch(() => {})
      router.refresh()
    },
  })

  async function saveLocale(locale: UiLocale) {
    const result = await updateMyUiLocale({ locale })
    if ("status" in result) {
      // Only app-owned error codes are translated; arbitrary thrown errors are never catalog keys.
      switch (result.errorMessage) {
        case "languageSignIn": return { ...result, errorMessage: t("languageSignIn") }
        case "languageUnavailable": return { ...result, errorMessage: t("languageUnavailable") }
        default: return { ...result, errorMessage: t("languageFailed") }
      }
    }
    return result
  }
}
